import { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { API } from '../lib/api';
import { Sparkle, PaperPlaneRight } from '@phosphor-icons/react';

export default function AIAssistant({ open, onClose, persona }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', meta: null }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok) throw new Error('AI assistant unavailable');
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === 'meta') {
            setSessionId(ev.session_id);
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], meta: ev }; return c; });
          } else if (ev.type === 'delta') {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.content }; return c; });
          } else if (ev.type === 'error') {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: `Error: ${ev.message}` }; return c; });
          }
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: e.message }; return c; });
    }
    setBusy(false);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0 backdrop-blur-xl bg-white/95" data-testid="ai-assistant-panel">
        <SheetHeader className="p-4 border-b border-slate-200">
          <SheetTitle className="font-display flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-blue-600" /> Governed Assistant</SheetTitle>
          <p className="font-mono-label text-[10px] uppercase text-slate-400">persona: {persona} · gpt-5.6-terra · policy-gated · audited</p>
        </SheetHeader>
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {messages.length === 0 && <p className="text-sm text-slate-400">Ask anything. Every answer shows its source and lands in the AI audit trail.</p>}
          {messages.map((m, i) => (
            <div key={i} className={`stagger-in ${m.role === 'user' ? 'text-right' : ''}`}>
              <div className={`inline-block max-w-[90%] rounded-md px-3 py-2 text-sm whitespace-pre-wrap text-left ${m.role === 'user' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-800'}`}>
                {m.content || (busy && i === messages.length - 1 ? '...' : '')}
              </div>
              {m.role === 'assistant' && m.meta && m.content && (
                <p className="font-mono-label text-[9px] uppercase text-slate-400 mt-1">src: {m.meta.source} · confidence: {m.meta.confidence} · audited</p>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-slate-200 flex gap-2">
          <input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Message the assistant..."
            className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          <button data-testid="ai-chat-send" onClick={send} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-3 transition-colors active:scale-[0.98]">
            <PaperPlaneRight size={16} weight="fill" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
